import dayjs from "dayjs";

import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import FlightIcon from "@mui/icons-material/Flight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import ModeIcon from "@mui/icons-material/Mode";
import StarPurple500Icon from "@mui/icons-material/StarPurple500";
import TurnedInNotIcon from "@mui/icons-material/TurnedInNot";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";

import InfiniteModeProInfo from "@/components/InfiniteModeProInfo";
import ProChip from "@/components/ProChip";
import { AlertTypeEnum } from "@/constants";
import { AIRLINE_NAME_MAP } from "@/constants/airline";
import { AIRPORT_MAP, COUNTRY_SUBD_LIST, REGION_LIST } from "@/constants/airport";
import { ROUTES_STOPS_OPTIONS, STOP_NUMBER_MAP } from "@/constants/filters";
import { bankCardImg } from "@/constants/image-map";
import { TimePoints } from "@/constants/timeRange";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";

import { LocationEnum } from "../Routes/hooks/useLocationFilter";
import { IAlertItem, getAirportNameByCode } from "./utils";

interface Props {
  data: IAlertItem;
  onEdit: () => void;
  onDelete: () => void;
  onChangeStatus: (v: boolean) => Promise<void>;
  onView: () => void;
  onDetail: () => void;
  changeStatusLoading: boolean;
  onInfiniteModeChange: (v: boolean) => Promise<void>;
  onShowTips: () => void;
  onAddLabel: () => void;
  onRemoveLabel: (v: string) => void;
}

const AlertItem = (props: Props) => {
  const { data, changeStatusLoading } = props;

  const { isMobile } = usePlatform();

  const filterData = data.filterData;
  const theme = useThemeData();
  const [loading, setLoading] = useState(false);

  const store = useGlobalStoreContext();

  const renderRightContent = () => {
    if (!data.expired && data.targetData && !(data.valid && data.viewed)) {
      let formattedDate = undefined;
      if (data.targetData.version) {
        formattedDate = dayjs(parseInt(data.targetData.version) * 1000).format("YYYY/MM/DD");
      }
      return (
        <div className="flex items-center justify-between">
          <div className="space-y-[6px]">
            <div className="flex items-center space-x-[10px]">
              {(data.targetData.lowest_miles?.bank || data.targetData.lowest_miles?.program) && (
                <div className="w-[40px] text-0">
                  {data.targetData.lowest_miles?.bank ? (
                    <img
                      className="w-[100%]"
                      src={bankCardImg[data.targetData.lowest_miles.bank]}
                      alt=""
                    />
                  ) : (
                    <img
                      className="w-[100%]"
                      src={
                        "https://img.awardtool.com/card_" +
                        data.targetData.lowest_miles.program.toLocaleUpperCase() +
                        ".png"
                      }
                      alt=""
                    />
                  )}
                </div>
              )}

              {data.targetData.lowest_miles?.miles && (
                <Typography fontSize={14}>{`${(+data.targetData.lowest_miles
                  ?.miles)?.toLocaleString()} pts`}</Typography>
              )}
              {data.targetData.lowest_miles?.tax && data.targetData.lowest_miles?.tax > 0 ? (
                // <Typography fontSize={14}>{`+$${data.targetData.lowest_miles?.tax}`}</Typography> : <Typography fontSize={14}>+tax</Typography>
                <Typography fontSize={14}>{`+$${data.targetData.lowest_miles?.tax}`}</Typography>
              ) : null}
            </div>
            <div className="flex space-x-[6px]">
              {data.targetData.additional_routes >= 1 && (
                <Chip
                  label={`${data.targetData.additional_routes}+ Flights`}
                  size="small"
                  sx={{
                    borderRadius: "7px",
                  }}
                />
              )}
              {!data.viewed && (
                <Chip
                  label={`New`}
                  size="small"
                  sx={{
                    borderRadius: "7px",
                  }}
                  color="primary"
                />
              )}
              {data.viewed && <Typography fontSize={14}>Viewed</Typography>}
            </div>
            {formattedDate && <Typography fontSize={14}>Seen on: {formattedDate}</Typography>}
          </div>
          <div className="flex flex-col items-center">
            {props.data.alert_type === AlertTypeEnum.Flight ? (
              <div className="flex items-center mt-[10px]">
                <Button variant="contained" onClick={props.onDetail}>
                  Details
                </Button>
              </div>
            ) : (
              <div className="flex items-center mt-[10px]">
                <Button variant="contained" onClick={props.onView}>
                  Search Real-Time
                </Button>
              </div>
            )}
            {props.data.alert_type === AlertTypeEnum.Flight && (
              <div className="flex justify-center mt-[6px]">
                <Link underline="hover" component="button" variant="caption" onClick={props.onView}>
                Search Real-Time
                </Link>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div className="text-center">
          <Typography className="!text-[14px]" color="grey">
            {data.valid
              ? "We're still in search of an eligible flight."
              : "Switch on the alert so we will find your ideal flight."}
          </Typography>
          {props.data.alert_type === AlertTypeEnum.Flight && (
            <div className="ml-[19px] mt-[2px]">
              <Link underline="hover" component="button" variant="caption" onClick={props.onView}>
                Search Real-Time
              </Link>
            </div>
          )}
          {/* <Typography className="!text-[14px]" color="grey">
            {!data.targetData
              ? "Don’t worry, we will continue searching for you"
              : !data.valid
              ? ""
              : ""}
          </Typography> */}
        </div>
      );
    }
  };

  const SwitchIcon = () => (
    <div
      className="flex items-center justify-center w-[20px] h-[20px] rounded-[50%]"
      style={{
        backgroundColor:
          data.valid && !changeStatusLoading
            ? theme.palette.primary.main
            : theme.palette.background.default,
      }}
    >
      {changeStatusLoading && <CircularProgress size={14} color="primary" thickness={6} />}
    </div>
  );

  const getLabelName = (value: string[]) => {
    return value
      ?.map((item) => {
        return getAirportNameByCode(item);
      })
      ?.filter(Boolean)
      ?.join(", ");
  };

  const getStops = (value: string) => {
    if (data.alert_type === AlertTypeEnum.Flight) {
      return STOP_NUMBER_MAP.get(value) || "Any number of stops";
    } else {
      return ROUTES_STOPS_OPTIONS.find((item) => item.value === value)?.name;
    }
  };

  const usedAlertCount =
    (dayjs(data.info.departureDateSec).startOf("day").diff(data.info.departureDate, "day") + 1) *
    data.info.departure.length *
    data.info.arrival.length;

  return (
    <Paper elevation={theme.isDarkMode ? 10 : 3} className="relative">
      <Chip
        size="small"
        className="absolute left-0 top-0 !rounded-none rounded-[50%]"
        color="primary"
        label={`alert count: ${usedAlertCount}`}
      />
      <Chip
        size="small"
        className="w-[130px] absolute right-0 top-0 !rounded-none !rounded-bl-3xl"
        color="primary"
        label={
          <div className="flex items-center space-x-[4px]">
            {data.alert_type === AlertTypeEnum.Panorama ? (
              <>
                <StarPurple500Icon sx={{ fontSize: "16px" }} />
                <span>panorama</span>
              </>
            ) : (
              <>
                <FlightIcon sx={{ fontSize: "16px" }} className="rotate-[45deg]" />
                <span>flight</span>
              </>
            )}
          </div>
        }
      />
      <div className="px-[15px] pt-[33px] pb-[15px] md:flex items-center justify-between">
        <div className="md:flex-1">
          <div className="flex mb-[6px] space-x-[10px]">
            {(data.filterData?.labelNames ?? []).map((labelName: string) => (
              <Chip
                size="small"
                key={labelName}
                label={labelName}
                onDelete={() => {
                  props.onRemoveLabel(labelName);
                }}
              />
            ))}
          </div>
          <div className="flex flex-wrap">
            <Typography>{getLabelName(data.info.departure || [])}</Typography>
            <Typography className="px-[6px]">-</Typography>
            <Typography sx={{ wordBreak: "break-word" }}>
              {getLabelName(data.info.arrival || [])}
            </Typography>
            <Tooltip
              sx={{ size: 10 }}
              title={"Add label to your alert to categorize them and make them easier to find."}
            >
              <LabelOutlinedIcon className="ml-[10px] cursor-pointer" onClick={props.onAddLabel} />
            </Tooltip>
          </div>

          <Typography fontSize={14} className="!mt-[4px] !mb-[2px]">
            {dayjs(data.info.departureDate).format("ddd, MMM DD YYYY")} -{" "}
            {dayjs(data.info.departureDateSec).format("ddd, MMM DD YYYY")}
          </Typography>

          <div className="flex">
            {props.data.alert_type === AlertTypeEnum.Flight && (
              <Typography fontSize={13} color="gray" className="!mr-[20px]">
                {filterData.passengers} adult{filterData.passengers > 1 ? "s" : ""}{" "}
                {data.info.children
                  ? data.info.children == 0
                    ? ""
                    : "with " +
                    data.info.children +
                    (data.info.children > 1 ? " children" : " child")
                  : ""}
              </Typography>
            )}
            <Typography fontSize={13} color="gray">
              {filterData.cabins.join(" & ")}
            </Typography>
          </div>

          {/* filter */}
          <div className="mt-[10px]">
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {getStops(filterData.stops) && (
                <Chip label={getStops(filterData.stops)} size="small" variant="outlined" />
              )}

              {filterData.banks?.length > 0 && (
                <Chip
                  label={
                    "Card: " +
                    (filterData.banks[0] === "All"
                      ? "All"
                      : filterData.banks[0] +
                      (filterData.banks.length - 1 > 0 ? ` +${filterData.banks.length - 1}` : ""))
                  }
                  size="small"
                  variant="outlined"
                />
              )}

              {filterData.airline_programs?.length > 0 && (
                <Chip
                  label={
                    "Program: " +
                    (filterData.airline_programs[0] === "All"
                      ? "All"
                      : AIRLINE_NAME_MAP[filterData.airline_programs[0]] +
                      (filterData.airline_programs.length - 1 > 0
                        ? ` +${filterData.airline_programs.length - 1}`
                        : ""))
                  }
                  size="small"
                  variant="outlined"
                />
              )}

              {filterData.max_points && (
                <Chip
                  label={"Max points: " + `${Number(filterData.max_points)?.toLocaleString()} pts`}
                  size="small"
                  variant="outlined"
                />
              )}

              {filterData.departure_time && (
                <Chip
                  label={
                    "Departure time: " +
                    filterData.departure_time?.map((v: string) => TimePoints[v]).join(" - ")
                  }
                  size="small"
                  variant="outlined"
                />
              )}

              {filterData.arrival_time && (
                <Chip
                  label={
                    "Arrival time: " +
                    filterData.arrival_time?.map((v: string) => TimePoints[v]).join(" - ")
                  }
                  size="small"
                  variant="outlined"
                />
              )}

              {filterData.flight_number && (
                <Chip
                  label={"Flight number: " + filterData.flight_number}
                  size="small"
                  variant="outlined"
                />
              )}

              {/* {typeof filterData.mixed_cabin === "number" && (
                <Chip
                  label={"Mixed cabin: " + filterData.mixed_cabin}
                  size="small"
                  variant="outlined"
                />
              )} */}

              {filterData.tax && (
                <Chip
                  label={"Max tax: $" + Number(filterData.tax)?.toLocaleString()}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </div>
        </div>

        <div className="mt-[20px] md:flex-1 md:mt-0 md:ml-[60px]">{renderRightContent()}</div>
      </div>
      <Divider />
      <div className="flex justify-between items-center p-[5px]">
        <div className="flex items-center">
          {data.expired ? (
            <div className="ml-[15px]">
              <Typography>Expired</Typography>
            </div>
          ) : (
            <>
              <Switch
                checkedIcon={<SwitchIcon />}
                icon={<SwitchIcon />}
                checked={data.valid}
                onChange={async (_, checked) => {
                  props.onChangeStatus(checked);
                }}
              />
              {data.valid ? "Alert On" : "Alert Off"}
            </>
          )}
        </div>
        <div className="flex items-center">
          <div className="flex justify-between w-[100%]">
            <FormControlLabel
              control={
                loading ? (
                  <div className="w-[42px] h-[42px] flex items-center justify-center">
                    <CircularProgress size={14} color="primary" thickness={6} />
                  </div>
                ) : (
                  <Checkbox
                    checked={data.infinite_mode}
                    onChange={async (_, checked) => {
                      if (store.show_upsell) {
                        props.onShowTips();
                        return;
                      }

                      try {
                        setLoading(true);
                        await props.onInfiniteModeChange(checked);
                        setLoading(false);
                      } catch (error) {
                        setLoading(false);
                      }
                    }}
                  />
                )
              }
              label={
                <div className="flex items-center">
                  <span>{isMobile ? "Infinite" : "Infinite mode"}</span>
                  <ProChip />
                </div>
              }
            />
            <Tooltip sx={{ size: 10 }} title={<InfiniteModeProInfo />}>
              <InfoOutlinedIcon sx={{ fontSize: 16, margin: "auto", ml: -1.5 }} />
            </Tooltip>
          </div>
          <IconButton aria-label="edit" size="small" onClick={() => props.onEdit()}>
            <ModeIcon fontSize="inherit" />
          </IconButton>
          <IconButton aria-label="delete" size="small" onClick={() => props.onDelete()}>
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        </div>
      </div>
    </Paper>
  );
};

export default AlertItem;
